import { useState, useCallback, useRef } from 'react';
import challanAnalyticsService from '../services/challanAnalyticsService';
import i18n from '../i18n';

const useChallanAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [productsTotal, setProductsTotal] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [customersTotal, setCustomersTotal] = useState(0);
    const [dailyData, setDailyData] = useState([]);
    const [challanList, setChallanList] = useState([]);
    const [listTotal, setListTotal] = useState(0);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const productsAbortRef = useRef(null);
    const customersAbortRef = useRef(null);
    const dailyAbortRef = useRef(null);
    const listAbortRef = useRef(null);

    const fetchSummary = useCallback(async (params = {}) => {
        setFetching(true);
        setError(null);
        try {
            const result = await challanAnalyticsService.getSummary(params);
            if (result.success) {
                setSummary(result.data);
            } else {
                setError(result.error);
                setSummary(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.challanAnalytics.fetchSummary'));
        } finally {
            setFetching(false);
        }
    }, []);

    const fetchProducts = useCallback(async (params = {}) => {
        if (productsAbortRef.current) {
            productsAbortRef.current.abort();
        }
        const controller = new AbortController();
        productsAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await challanAnalyticsService.getProducts(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setProducts(raw?.products ?? []);
                setProductsTotal(raw?.total ?? 0);
            } else {
                setError(result.error);
                setProducts([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.products.fetchList'));
        } finally {
            if (productsAbortRef.current === controller) {
                setFetching(false);
                productsAbortRef.current = null;
            }
        }
    }, []);

    const fetchCustomers = useCallback(async (params = {}) => {
        if (customersAbortRef.current) {
            customersAbortRef.current.abort();
        }
        const controller = new AbortController();
        customersAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await challanAnalyticsService.getCustomers(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setCustomers(raw?.customers ?? []);
                setCustomersTotal(raw?.total ?? 0);
            } else {
                setError(result.error);
                setCustomers([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.customers.fetchList'));
        } finally {
            if (customersAbortRef.current === controller) {
                setFetching(false);
                customersAbortRef.current = null;
            }
        }
    }, []);

    const fetchDaily = useCallback(async (params = {}) => {
        if (dailyAbortRef.current) {
            dailyAbortRef.current.abort();
        }
        const controller = new AbortController();
        dailyAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await challanAnalyticsService.getDaily(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                setDailyData(result.data?.daily ?? []);
            } else {
                setError(result.error);
                setDailyData([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.challanAnalytics.fetchDaily'));
        } finally {
            if (dailyAbortRef.current === controller) {
                setFetching(false);
                dailyAbortRef.current = null;
            }
        }
    }, []);

    const fetchList = useCallback(async (params = {}) => {
        if (listAbortRef.current) {
            listAbortRef.current.abort();
        }
        const controller = new AbortController();
        listAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await challanAnalyticsService.getList(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setChallanList(raw?.items ?? raw?.data ?? (Array.isArray(raw) ? raw : []));
                setListTotal(raw?.total ?? raw?.count ?? (Array.isArray(raw) ? raw.length : 0));
            } else {
                setError(result.error);
                setChallanList([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.challanAnalytics.fetchList'));
        } finally {
            if (listAbortRef.current === controller) {
                setFetching(false);
                listAbortRef.current = null;
            }
        }
    }, []);

    return {
        summary,
        products,
        productsTotal,
        customers,
        customersTotal,
        dailyData,
        challanList,
        listTotal,
        fetching,
        error,
        fetchSummary,
        fetchProducts,
        fetchCustomers,
        fetchDaily,
        fetchList,
    };
};

export default useChallanAnalytics;
